import { useAbility } from '@casl/react';
import {
  Box,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Tooltip,
  Typography,
  lighten,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import EntityIcon from 'src/components/Icons/EntityIcon';
import { useAdminUrlNavigation } from 'src/hooks/useAdminUrlNavigation';
import useEffectiveApplicationGroupId from 'src/hooks/useEffectiveApplicationGroupId';
import useEffectiveApplicationId from 'src/hooks/useEffectiveApplicationId';
import pages from 'src/router/routes';
import WidgetCard from 'switch-fe-shared/components/Cards/WidgetCard';

import { formatNumber } from '../../../../../../switch-shared/src/utils/formatters';
import { ACTIONS } from '../../../../auth/utils/defineAbilityFor';
import { AbilityContext } from '../../../../context/canContext';
import { useDashboard } from '../../../../context/dashboardContext';
import { useGetOnlineGatewaysQuery } from '../../../../services/switch-rest-api/gateways.service';
import { isAllSelection, isUtilityType } from '../../../../utils/selection';
import { useAllGroupsMetrics } from '../../hooks/useAllGroupsMetrics';

const GatewaysWidget = () => {
  /* Hooks & Use Effects */

  const theme = useTheme();
  const navigate = useNavigate();
  const { getApplicationScopedPath } = useAdminUrlNavigation();
  const { utility } = useDashboard();
  const appGroupId = useEffectiveApplicationGroupId();
  const appId = useEffectiveApplicationId();
  const ability = useAbility(AbilityContext);
  const isAllGroupsSelected = isAllSelection(appGroupId);
  const isAllAppsSelected = isAllSelection(appId);

  /* States */

  /* All Groups Metrics Hook */
  const {
    metrics: allGroupsMetrics,
    isLoading: isAllGroupsLoading,
    isFetching: isAllGroupsFetching,
  } = useAllGroupsMetrics();

  /* API Calls */

  const { data, isLoading, isFetching, isSuccess, refetch } =
    useGetOnlineGatewaysQuery(
      {
        ...(!isAllAppsSelected &&
          appId != null && {
            applicationId: appId,
          }),
        ...(!isAllGroupsSelected && {
          applicationGroupId: appGroupId,
        }),
        ...(isUtilityType(utility) && { utilityType: utility }),
      },
      {
        skip:
          !ability.can(ACTIONS.READ, 'Gateways') ||
          appGroupId == null ||
          isAllGroupsSelected,
        pollingInterval: 30_000,
      }
    );

  /* Functions & Functional Components */

  const getColor = (onlineCount: number, totalCount: number) => {
    if (totalCount === 0) return theme.tokens.colors.mutedForeground;
    const percentage = (onlineCount / totalCount) * 100;
    if (percentage >= 90) {
      return theme.palette.success.main;
    }
    if (percentage >= 70) {
      return theme.palette.warning.main;
    }
    return theme.palette.error.main;
  };

  return (
    <WidgetCard
      title='Gateways'
      subheader={'Total count and online status'}
      tooltipDescription={
        isAllGroupsSelected
          ? 'Count and online status of all installed gateways across all groups'
          : `Count and online status of the installed gateways in the selected ${
              appId != null && !isAllSelection(appId) ? 'application' : 'group'
            }`
      }
      avatar={
        <Box
          sx={{
            padding: `${theme.tokens.spacing.sm}px`,
            height: `${theme.tokens.spacing.sm * 5}px`,
            width: `${theme.tokens.spacing.sm * 5}px`,
            mx: `-${theme.tokens.spacing.sm}px`,
            borderRadius: theme.tokens.borderRadius.full,
            background: theme.tokens.colors.card,
            boxShadow: theme.tokens.shadows.sm,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.palette.primary.main,
          }}
        >
          <EntityIcon type='gateways' />
        </Box>
      }
      disabled={!ability.can(ACTIONS.READ, 'Gateways')}
      onClickFn={
        !isAllAppsSelected
          ? () =>
              navigate(
                getApplicationScopedPath('gateways', pages.gateways.path)
              )
          : undefined
      }
      cardContentProps={{
        sx: {
          pt: `0 !important`,
          pb: `16px !important`,
          color: lighten(theme.palette.primary.main, 0.1),
        },
      }}
    >
      <>
        {isAllGroupsSelected ? (
          <>
            {isAllGroupsLoading || isAllGroupsFetching ? (
              <Grid
                container
                height={'100%'}
                alignContent={'center'}
                justifyContent={'center'}
              >
                <Grid item>
                  <CircularProgress />
                </Grid>
              </Grid>
            ) : (
              <Grid
                container
                alignContent={'center'}
                justifyContent={'center'}
                height={'100%'}
              >
                {allGroupsMetrics != null && (
                  <Grid item xs={12}>
                    <Tooltip
                      slotProps={{
                        tooltip: {
                          sx: {
                            background: 'white',
                            color: theme.tokens.colors.cardForeground,
                            boxShadow: theme.tokens.shadows.lg,
                            borderRadius: theme.tokens.borderRadius.md,
                            padding: 0,
                            minWidth: 175,
                          },
                        },
                      }}
                      title={
                        <Box>
                          <Stack
                            spacing={1}
                            sx={{
                              px: `${theme.tokens.spacing.md}px`,
                              py: `${theme.tokens.spacing.sm}px`,
                            }}
                          >
                            <Stack
                              direction='row'
                              alignItems='center'
                              justifyContent='space-between'
                              spacing={2}
                            >
                              <Stack
                                direction='row'
                                alignItems='center'
                                spacing={1}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius:
                                      theme.tokens.borderRadius.full,
                                    backgroundColor: theme.palette.success.main,
                                  }}
                                />
                                <Typography
                                  variant='body2'
                                  sx={{ color: theme.palette.text.secondary }}
                                >
                                  Online
                                </Typography>
                              </Stack>
                              <Typography
                                variant='body2'
                                sx={{
                                  fontWeight:
                                    theme.tokens.typography.fontWeight.semibold,
                                }}
                              >
                                {formatNumber(
                                  allGroupsMetrics?.gateways.online ?? 0,
                                  0
                                )}
                              </Typography>
                            </Stack>
                            <Stack
                              direction='row'
                              alignItems='center'
                              justifyContent='space-between'
                              spacing={2}
                            >
                              <Stack
                                direction='row'
                                alignItems='center'
                                spacing={1}
                              >
                                <Box
                                  sx={{
                                    width: 8,
                                    height: 8,
                                    borderRadius:
                                      theme.tokens.borderRadius.full,
                                    backgroundColor: theme.palette.error.main,
                                  }}
                                />
                                <Typography
                                  variant='body2'
                                  sx={{ color: theme.palette.text.secondary }}
                                >
                                  Offline
                                </Typography>
                              </Stack>
                              <Typography
                                variant='body2'
                                sx={{
                                  fontWeight:
                                    theme.tokens.typography.fontWeight.semibold,
                                }}
                              >
                                {formatNumber(
                                  allGroupsMetrics?.gateways.offline ?? 0,
                                  0
                                )}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Box>
                      }
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Typography
                            variant='body2'
                            sx={{
                              color: theme.palette.primary.main,
                              fontWeight: 700,
                              fontSize: '25px',
                              display: 'inline-block',
                              mr: `${theme.tokens.spacing.sm}px`,
                            }}
                          >
                            {formatNumber(
                              allGroupsMetrics?.gateways.total ?? 0,
                              0
                            )}
                          </Typography>
                          <Chip
                            label={`${(
                              allGroupsMetrics?.gateways.onlinePercentage ?? 0
                            ).toFixed(0)}%`}
                            sx={{
                              backgroundColor: getColor(
                                allGroupsMetrics?.gateways.online ?? 0,
                                allGroupsMetrics?.gateways.total ?? 0
                              ),
                              color: theme.palette.common.white,
                              fontWeight: 600,
                              fontSize: theme.tokens.typography.fontSize.lg,
                              height: 28,
                              cursor: 'default',
                            }}
                          />
                        </Box>
                      </Box>
                    </Tooltip>
                  </Grid>
                )}
              </Grid>
            )}
          </>
        ) : isLoading || isFetching ? (
          <Grid
            container
            height={'100%'}
            alignContent={'center'}
            justifyContent={'center'}
          >
            <Grid item>
              <CircularProgress />
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            alignContent={'center'}
            justifyContent={'center'}
            height={'100%'}
          >
            {data != null && (
              <Grid item xs={12}>
                {data.onlineCount + data.offlineCount === 0 ? (
                  <Box
                    sx={{
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                    }}
                  >
                    <Typography variant='body2'>No gateways found</Typography>
                  </Box>
                ) : (
                  <Tooltip
                    slotProps={{
                      tooltip: {
                        sx: {
                          background: 'white',
                          color: theme.tokens.colors.cardForeground,
                          boxShadow: theme.tokens.shadows.lg,
                          borderRadius: theme.tokens.borderRadius.md,
                          padding: 0,
                          minWidth: 175,
                        },
                      },
                    }}
                    title={
                      <Box>
                        <Stack
                          spacing={1}
                          sx={{
                            px: `${theme.tokens.spacing.md}px`,
                            py: `${theme.tokens.spacing.sm}px`,
                          }}
                        >
                          <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            spacing={2}
                          >
                            <Stack
                              direction='row'
                              alignItems='center'
                              spacing={1}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: theme.tokens.borderRadius.full,
                                  backgroundColor: theme.palette.success.main,
                                }}
                              />
                              <Typography
                                variant='body2'
                                sx={{ color: theme.palette.text.secondary }}
                              >
                                Online
                              </Typography>
                            </Stack>
                            <Typography
                              variant='body2'
                              sx={{
                                fontWeight:
                                  theme.tokens.typography.fontWeight.semibold,
                              }}
                            >
                              {formatNumber(data.onlineCount, 0)}
                            </Typography>
                          </Stack>
                          <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='space-between'
                            spacing={2}
                          >
                            <Stack
                              direction='row'
                              alignItems='center'
                              spacing={1}
                            >
                              <Box
                                sx={{
                                  width: 8,
                                  height: 8,
                                  borderRadius: theme.tokens.borderRadius.full,
                                  backgroundColor: theme.palette.error.main,
                                }}
                              />
                              <Typography
                                variant='body2'
                                sx={{ color: theme.palette.text.secondary }}
                              >
                                Offline
                              </Typography>
                            </Stack>
                            <Typography
                              variant='body2'
                              sx={{
                                fontWeight:
                                  theme.tokens.typography.fontWeight.semibold,
                              }}
                            >
                              {formatNumber(data.offlineCount, 0)}
                            </Typography>
                          </Stack>
                        </Stack>
                      </Box>
                    }
                  >
                    <Box
                      sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography
                          variant='body2'
                          sx={{
                            color: theme.palette.primary.main,
                            fontWeight: 700,
                            fontSize: '25px',
                            display: 'inline-block',
                            mr: `${theme.tokens.spacing.sm}px`,
                          }}
                        >
                          {formatNumber(
                            data.onlineCount + data.offlineCount,
                            0
                          )}
                        </Typography>
                        <Chip
                          label={`${(
                            (data.onlineCount /
                              (data.onlineCount + data.offlineCount)) *
                            100
                          ).toFixed(0)}%`}
                          sx={{
                            backgroundColor: getColor(
                              data.onlineCount,
                              data.onlineCount + data.offlineCount
                            ),
                            fontWeight: 600,
                            fontSize: theme.tokens.typography.fontSize.lg,
                            height: 28,
                            cursor: 'default',
                          }}
                        />
                      </Box>
                    </Box>
                  </Tooltip>
                )}
              </Grid>
            )}
          </Grid>
        )}
      </>
    </WidgetCard>
  );
};

export default GatewaysWidget;
